import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalyticsContext } from '../../../../context/analytics-context';
import { EnvelopeType } from '../../../../global-types/envelope';
import { useFeatureFlags } from '../../../../hooks';
import { useIsOneToMany } from '../../../../hooks/use-is-one-to-many';
import { useEnvelopeSelection } from '../../../select-envelope/hooks/use-envelope-selection';
import { useAddressValidation } from '../../hooks/use-address-validation';
import { AddressBox } from '../address-box/address-box';
import { USPSValidationDialog } from '../usps-validation-dialog/usps-validation-dialog';
import { AddressConfirmationProps } from './address-confirmation-types';
import styles from './address-confirmation.module.scss';

// AddressConfirmation is used to display the address confirmation dialog
// and handles the 1:1 address confirmation flow
// and old 1:M cards with out r-r addresses
export const AddressConfirmation = ({
  isOpen,
  onClose,
  addressesToValidate,
  onSkipValidation,
  onEditAddress,
  isAddressFromAddressBook = false,
  submitEditedAddress,
  onResubmitAddress,
}: AddressConfirmationProps) => {
  const { t } = useTranslation();
  const isOneToMany = useIsOneToMany();
  const { IS_MULT_ADDRESS_ENABLED } = useFeatureFlags();
  const { selectedEnvelope } = useEnvelopeSelection();
  const { trackUSPSValidationManualAddressReturnOrRecipient } = useAnalyticsContext();

  // 1:1, sns, 1:M (return-only) address validation
  const {
    validationTitle: title,
    validationMessageKey: messageKey,
    addressInfo,
    handleConfirm,
    handleEditAddress,
  } = useAddressValidation({
    addressesToValidate,
    onSkipValidation,
    onEditAddress,
    isAddressFromAddressBook,
    submitEditedAddress,
  });
  const isReturnAddress = title.includes('return');
  const addressType = isReturnAddress ? 'return' : 'recipient';

  const shouldHandleReturnRecipient =
    isOneToMany && IS_MULT_ADDRESS_ENABLED && selectedEnvelope !== EnvelopeType.Return;

  // handles the decision to handle the 1:M(return-recipient) or the 1:1/sns/1:M(return-only) confirmation
  const handleOnConfirm = () => {
    trackUSPSValidationManualAddressReturnOrRecipient({ addressType, action: 'confirm address' });
    if (shouldHandleReturnRecipient && onResubmitAddress) {
      onResubmitAddress();
      return;
    }
    handleConfirm();
  };

  // handles the decision to handle the 1:M(return-recipient) or the 1:1/sns/1:M(return-only) edit address
  const handleOnEditAddress = () => {
    trackUSPSValidationManualAddressReturnOrRecipient({ addressType, action: 'edit address' });
    if (shouldHandleReturnRecipient) {
      onClose();
      return;
    }
    handleEditAddress();
  };

  const onCloseDialog = () => {
    trackUSPSValidationManualAddressReturnOrRecipient({ addressType, action: 'close' });
    onClose();
  };

  const message = t(messageKey);
  const usableAddress = t('addressConfirmation.usableAddress');

  const USPSMessage = () => {
    return (
      <>
        <p>{message}</p>
        <b className={styles['validation-message-notice']}>{usableAddress}</b>
      </>
    );
  };

  return (
    <USPSValidationDialog
      title={title}
      message={<USPSMessage />}
      isOpen={isOpen}
      onClose={onCloseDialog}
      onConfirm={handleOnConfirm}
    >
      <AddressBox address={addressInfo} onEditAddress={handleOnEditAddress} />
    </USPSValidationDialog>
  );
};
